// src/app/services/analytics/outcomes-api.ts
//
// The Outcomes view payload: recommendation movement set against demand.
//
// Every series carries its own `source`. "measurement" means real numbers from
// this client's measurement runs; "illustrative" means sample data shaped like
// a real engagement; "waiting" means the Google property IS connected and has
// not handed over a day yet, which is an absence with a reason and NOT sample
// data. A waiting series has no points and no baseline, and the pane draws its
// axis and says what it is waiting for.
//
// The payload-level `illustrative` and `waiting` flags drive the on-screen
// label. Do not suppress that label, and do not render a series without
// honouring its source.
import { apiFetch } from "@/api/api-fetch";

export type SeriesSource = "measurement" | "illustrative" | "waiting";

/** [week, value] pairs. Week is 1-based on the view's shared clock. */
export type SeriesPoint = [number, number];

export interface OutcomesSeries {
  source: SeriesSource;
  label: string;
  points: SeriesPoint[];
  /** Null on a waiting series: there is nothing to take a quiet mean of, and a
   *  zero there would be a measurement of nothing. */
  baseline: number | null;
  /** What the property this was read from is called, as its owner names it.
   *  Present on a MEASURED demand series only, and absent rather than empty when
   *  the connection cannot say. */
  property?: string;
  runs?: Array<{
    week: number;
    runId: number | null;
    sequence: number | null;
    completedAt: string | null;
    runCount: number;
  }>;
}

export interface OutcomesPayload {
  brand: string;
  /** True when ANY series on the view is sample data. Drives the label. */
  illustrative: boolean;
  /** True when either demand pane is connected and waiting for its first day.
   *  Never rendered as a pill at the same time as `illustrative`. */
  waiting: boolean;
  weeks: number;
  recommendation: OutcomesSeries;
  brandedSearch: OutcomesSeries;
  conversions: OutcomesSeries;
  /** Null when the series is too short to have established a noise range. */
  noiseBand: { lo: number; hi: number; exitWeek: number | null; source: SeriesSource } | null;
  responseWindows: Array<{ from: number; to: number }>;
  events: Array<{ week: number; key: string; label: string }>;
  eventsSource: SeriesSource;
  eventStudy: {
    source: SeriesSource;
    tMin: number;
    tMax: number;
    series: number[][];
    clients: number;
  };
}

export async function fetchOutcomes(brand: string): Promise<OutcomesPayload> {
  const params = new URLSearchParams();
  params.set("brand", brand);
  return apiFetch<OutcomesPayload>(`/analytics/outcomes?${params.toString()}`);
}
